import { useRef } from "react";
import { motion, ease as mmEase, useInView } from "@/lib/motion";

const ez = mmEase.expo;

const STATS = [
  { value: "$700B", desc: "Sent home by diaspora families every year",                          emerald: false },
  { value: "$84B",  desc: "Taken in fees annually — more than all foreign aid combined",        emerald: false },
  { value: "0.5%",  desc: "All MoneyMeet Pay ever charges — flat, transparent, nothing hidden", emerald: true  },
];

// The Weight: fee comparison Thread visualization
function WeightComparison({ inView }) {
  const bankPathId  = "tw-bank-path";
  const mmPathId    = "tw-mm-path";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: ez, delay: 0.52 }}
      style={{
        background: "var(--color-section-subtle)",
        border: "1px solid rgba(11,29,58,0.07)",
        borderRadius: "16px",
        padding: "28px 32px",
        marginTop: "40px",
      }}
    >
      <p style={{
        fontSize: "10px", fontWeight: 700, letterSpacing: "0.18em",
        textTransform: "uppercase", color: "rgba(11,29,58,0.35)",
        marginBottom: "20px",
      }}>
        The same £1,000 — two paths
      </p>

      {/* Bank thread — thick, slow */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <span style={{ fontSize: "11px", fontWeight: 600, color: "rgba(11,29,58,0.45)" }}>Traditional bank</span>
          <span style={{ fontSize: "11px", fontWeight: 700, color: "rgba(11,29,58,0.55)", fontVariantNumeric: "tabular-nums" }}>
            5% fee · 3 days
          </span>
        </div>
        <svg viewBox="0 0 500 28" fill="none" style={{ width: "100%", overflow: "visible" }}>
          <path
            id={bankPathId}
            d="M 0 14 C 120 6, 240 22, 380 14 S 460 8, 500 14"
            stroke="rgba(11,29,58,0.18)" strokeWidth="3" strokeLinecap="round" fill="none"
            strokeDasharray="5 4"
            style={{ animation: inView ? "dashMove 2.8s linear infinite" : "none" }}
          />
          {inView && (
            <circle r="5.5" fill="rgba(11,29,58,0.28)" opacity="0.7">
              <animateMotion dur="4.5s" repeatCount="indefinite">
                <mpath href={`#${bankPathId}`} />
              </animateMotion>
            </circle>
          )}
          <text x="490" y="18" fontSize="10" fill="rgba(11,29,58,0.35)" textAnchor="end">£950</text>
        </svg>
      </div>

      {/* MoneyMeet thread — thin, fast */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <span style={{ fontSize: "11px", fontWeight: 700, color: "#059669" }}>MoneyMeet Pay</span>
          <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-success)", fontVariantNumeric: "tabular-nums" }}>
            0.5% fee · ~2 min
          </span>
        </div>
        <svg viewBox="0 0 500 28" fill="none" style={{ width: "100%", overflow: "visible" }}>
          <defs>
            <linearGradient id="twMMGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor="var(--color-success)" stopOpacity="0.4" />
              <stop offset="50%"  stopColor="var(--color-success)" />
              <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.6" />
            </linearGradient>
            <filter id="twGlow">
              <feGaussianBlur stdDeviation="3" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          <path
            id={mmPathId}
            d="M 0 14 C 80 4, 200 24, 320 14 S 440 4, 500 14"
            stroke="url(#twMMGrad)" strokeWidth="1.5" strokeLinecap="round" fill="none"
            strokeDasharray="6 4"
            style={{ animation: inView ? "dashMove 0.9s linear infinite" : "none" }}
          />
          {inView && (
            <>
              <circle r="8" fill="var(--color-success)" opacity="0.15">
                <animateMotion dur="1.5s" repeatCount="indefinite">
                  <mpath href={`#${mmPathId}`} />
                </animateMotion>
              </circle>
              <circle r="4" fill="var(--color-success)" filter="url(#twGlow)">
                <animateMotion dur="1.5s" repeatCount="indefinite">
                  <mpath href={`#${mmPathId}`} />
                </animateMotion>
              </circle>
              <circle r="2" fill="white">
                <animateMotion dur="1.5s" repeatCount="indefinite">
                  <mpath href={`#${mmPathId}`} />
                </animateMotion>
              </circle>
            </>
          )}
          <text x="490" y="18" fontSize="10" fill="var(--color-success)" textAnchor="end" fontWeight="700">£995</text>
        </svg>
      </div>

      <p style={{
        fontSize: "11px", color: "rgba(11,29,58,0.35)",
        marginTop: "12px", fontStyle: "italic",
      }}>
        Same amount. Same destination. £45 difference — every single time.
      </p>
    </motion.div>
  );
}

export default function TruthSection() {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px 0px" });

  return (
    <section ref={ref} className="py-24 px-6 relative" style={{ background: "var(--color-section-cream)" }}>

      <div className="max-w-[1200px] mx-auto">

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: ez }}
          className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald mb-5"
        >
          Why we exist
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(4px)" }}
          animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
          transition={{ duration: 0.72, ease: ez, delay: 0.06 }}
          className="font-display font-black text-navy text-balance mb-12"
          style={{ fontSize: "clamp(1.75rem, 3.8vw, 3rem)", letterSpacing: "-0.035em", lineHeight: 1.1 }}
        >
          The global financial system wasn't<br className="hidden sm:block" />
          designed for people who left home.
        </motion.h2>


        <div className="grid sm:grid-cols-3 gap-8 sm:gap-12 mb-10">
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.value}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.58, ease: ez, delay: 0.22 + i * 0.1 }}
            >
              <p
                className={`font-display font-black mb-3 num-tabular leading-none ${stat.emerald ? "text-emerald" : "text-navy"}`}
                style={{
                  fontSize: "clamp(2.2rem, 5vw, 3.75rem)",
                  letterSpacing: "-0.04em",
                }}
              >
                {stat.value}
              </p>
              <p className="text-[14px] leading-relaxed" style={{ color: "rgba(11,29,58,0.50)" }}>
                {stat.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* The Weight: Thread fee comparison visualization */}
        <WeightComparison inView={inView} />

        <motion.blockquote
          initial={{ opacity: 0, x: -16 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, ease: ez, delay: 0.62 }}
          className="border-l-[3px] border-emerald pl-6 max-w-xl mt-14"
        >
          <p className="text-[1.05rem] italic leading-relaxed mb-3" style={{ color: "rgba(11,29,58,0.62)" }}>
            "When I realized I was losing £35 every month just to send money home, I wasn't angry at the bank.
            I was angry at myself for not realizing sooner."
          </p>
          <p className="text-[12px] font-semibold" style={{ color: "rgba(11,29,58,0.40)" }}>
            Kwame A., Software Engineer, Toronto
          </p>
        </motion.blockquote>

      </div>
    </section>
  );
}
